import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Preloader } from '@common-fe/common-fe';
import _ from 'lodash';

import { WarnModal } from '@/components/elements';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { Receipt } from '@/modules/MyDocuments/myDocuments.types';
import useRemoveDocumentQuery from '@/modules/transaction/queries/useRemoveDocument.query';

const CANNOT_DELETE_MESSAGE = 'Details: Can not delete active receipt';

interface Props {
  deletedItem?: Receipt;
  onSetId: (id: string) => void;
}
const ReceiptDeleteModal: React.FC<Props> = ({ deletedItem, onSetId }) => {
  const { t } = useTranslation();
  const { remove: handleDelete } = useRemoveDocumentQuery();
  const [loading, setLoading] = useState(false);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const [errorModalVisible, setErrorModalVisible] = useState(false);

  const handleClose = useCallback(() => {
    onSetId('');
  }, [onSetId]);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    try {
      await handleDelete(deletedItem?.documentId || '');
      onSetId('');
      handleAddPermanentSnackbar({
        text: `${deletedItem?.fileName || ''} has been deleted.`,
        state: SnackbarState.negative,
        closeIcon: true,
      });
    } catch (e) {
      const errorMessage = _.get(e, 'response.data.elevate_error_message', '') as string;
      const isCannotDelete = errorMessage?.includes(CANNOT_DELETE_MESSAGE);

      if (isCannotDelete) {
        setErrorModalVisible(true);
      }
    } finally {
      setLoading(false);
    }
  }, [handleDelete, deletedItem, onSetId, handleAddPermanentSnackbar]);

  if (!deletedItem) return null;

  return (
    <>
      <WarnModal
        testId="receipt-delete"
        visible={!!deletedItem && !errorModalVisible}
        header="Delete this Receipt?"
        onSetVisible={handleClose}
        cancelButtonNode={
          <Box direction="row">
            <Box width="150px" margin={{ right: 'spacing6' }}>
              <AppButton
                testId="receipt-delete_cancel"
                buttonType="secondary"
                onClick={handleClose}
                width="100%"
                disabled={loading}
              >
                {t('Cancel')}
              </AppButton>
            </Box>
            <Box width="150px" margin={{ left: 'spacing6' }}>
              <AppButton
                testId="receipt-delete_apply"
                color="red"
                onClick={handleConfirm}
                width="100%"
                disabled={loading}
              >
                {loading ? <Preloader color="white" /> : t('Yes, Delete')}
              </AppButton>
            </Box>
          </Box>
        }
      />

      {/* Error Modal */}
      <WarnModal
        visible={errorModalVisible}
        header="Can’t Delete Receipt"
        helptext="You can’t delete this Receipt because it is associated with an approved claim."
        onSetVisible={() => {
          setErrorModalVisible(false);
          handleClose();
        }}
        cancelButtonNode={
          <Box width="150px" margin={{ left: 'auto', right: 'auto' }}>
            <AppButton
              buttonType="primary"
              onClick={() => {
                setErrorModalVisible(false);
                handleClose();
              }}
              width="100%"
            >
              {t('Close')}
            </AppButton>
          </Box>
        }
      />
    </>
  );
};

export default ReceiptDeleteModal;
